import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

/**
 * @file
 * @module Pages/AdminUser
 * @description AdminUser est une page qui affiche les détails d'un utilisateur spécifique.
 * 
 * - Elle utilise le hook useParams de 'react-router-dom' pour obtenir l'ID de l'utilisateur à partir de l'URL.
 * - Elle utilise ensuite l'ID pour faire une requête HTTP GET à l'API et obtenir les détails de l'utilisateur.
 * - Elle affiche ensuite ces détails à l'administrateur.
 * - Elle fournit également un formulaire pour mettre à jour les détails de l'utilisateur, qui fait une requête HTTP PUT à l'API.
 * - Elle fournit également une fonction pour supprimer l'utilisateur, qui fait une requête HTTP DELETE à l'API.
 * - Cette page utilise le hook useState de React pour gérer l'état de l'utilisateur.
 * -  Utilise le hook useEffect de React pour appeler l'API lors du chargement de la page.
 * -  Utilise le hook useSelector de 'react-redux' pour accéder au token de l'utilisateur dans le store Redux.
 * -  Utilise le hook useNavigate de 'react-router-dom' pour naviguer vers une autre page après la suppression de l'utilisateur.
 * 
 */
const apiPrefix = process.env.REACT_APP_API_PREFIX;

const UserDetail = () => {
    const { id } = useParams(); 
    const [user, setUser] = useState(null); 
    const [showModal, setShowModal] = useState(false);
    const [message, setMessage] = useState('');
    const navigate = useNavigate(); 
    const token = useSelector((state) => state.user.token);

    const hide = () => {
        setShowModal(false);
        navigate('/admin/users'); 
    };

    useEffect(() => {
        // Appeler l'API lorsque l'ID de l'utilisateur change
        //URL DEV
        //   fetch(`http://localhost:3001/users/${id}`)
        //URL PROD
        fetch(apiPrefix +`users/${id}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })
            .then(response => response.json())
            .then(data => {
                setUser(data); 
            })
            .catch(error => {
                console.error('Erreur lors de la récupération des détails de l\'utilisateur', error);
            });
    }, [id, token]);

    const handleDelete = () => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) {
            // Supprimer l'utilisateur
            // URL DEV
            //fetch(`http://localhost:3001/users/${id}`, {
            // URL PROD
            fetch(apiPrefix +`users/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`, 
                },
            })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Erreur lors de la suppression de l\'utilisateur');
                }
                navigate('/'); 
            })
            .catch(error => {
                console.error('Erreur lors de la suppression de l\'utilisateur', error);
            });
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();
    // console.log(user)
        // Mettre à jour l'utilisateur
        //URL DEV
        //  fetch(`http://localhost:3001/admin/update/${id}`, {
        // URL PROD
        fetch(apiPrefix +`admin/update/${id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`, 
            },
            body: JSON.stringify(user),
        })
        .then(response => response.json())
        .then(data => {
            setMessage('Profil mis à jour avec succès');
            setShowModal(true);
        })
        .catch(error => {
            console.error('Erreur lors de la mise à jour du profil', error);
        });
    };

    if (!user) {
        return (
            <div className="container-responsive section-padding">
                <div className="flex justify-center items-center min-h-[200px]">
                    <div className="loading-spinner"></div>
                    <span className="ml-3 text-gray-600">Chargement des détails utilisateur...</span>
                </div>
            </div>
        ); 
    }

    const handleChange = (event) => {
        const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
        setUser({ ...user, [event.target.name]: value });
    };
    
    return (
        <div className="container-responsive section-padding">
            <div className="max-w-4xl mx-auto">
                <div className="mb-6">
                    <Link 
                        to="/admin/users" 
                        className="btn-secondary inline-flex items-center"
                    >
                        <i className="fas fa-arrow-left mr-2"></i>
                        Retour à la page des utilisateurs
                    </Link>
                </div>

                <div className="card mb-6">
                    <h1 className="text-3xl font-bold text-primary mb-4">{user.username}</h1>
                    <div className="alert alert-info">
                        <i className="fas fa-clock mr-2"></i>
                        {user.lastactive === null ? 
                            "Pas d'activité récente" : 
                            `Dernière activité: ${new Date(user.lastactive).toLocaleString()}`
                        }
                    </div>
                </div>

                <div className="card">
                    <h2 className="text-2xl font-bold text-gray-800 mb-6">Modifier les informations utilisateur</h2>
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid gap-6 md:grid-cols-2">
                            <div className="form-group">
                                <label className="form-label">
                                    <i className="fas fa-envelope mr-2.5 text-primary"></i>
                                    Nom d'utilisateur
                                </label>
                                <input 
                                    type="text" 
                                    name="username" 
                                    value={user.username} 
                                    onChange={handleChange}
                                    className="form-input"
                                />
                            </div>
                            
                            <div className="form-group">
                                <label className="form-label">
                                    <i className="fas fa-user mr-2.5 text-primary"></i>
                                    Prénom
                                </label>
                                <input 
                                    type="text" 
                                    name="firstname" 
                                    value={user.firstname} 
                                    onChange={handleChange}
                                    className="form-input"
                                />
                            </div>
                            
                            <div className="form-group">
                                <label className="form-label">
                                    <i className="fas fa-user mr-2.5 text-primary"></i>
                                    Nom
                                </label>
                                <input 
                                    type="text" 
                                    name="lastname" 
                                    value={user.lastname} 
                                    onChange={handleChange}
                                    className="form-input"
                                />
                            </div>
                            
                            <div className="form-group">
                                <label className="form-label">
                                    <i className="fas fa-building mr-2.5 text-primary"></i>
                                    Société
                                </label>
                                <input 
                                    type="text" 
                                    name="company" 
                                    value={user.company} 
                                    onChange={handleChange}
                                    className="form-input"
                                />
                            </div>
                            
                            <div className="form-group">
                                <label className="form-label">
                                    <i className="fas fa-hashtag mr-2.5 text-primary"></i>
                                    SIRET
                                </label>
                                <input 
                                    type="text" 
                                    name="siret" 
                                    value={user.siret} 
                                    onChange={handleChange}
                                    className="form-input"
                                />
                            </div>
                        </div>

                        <div className="border-t pt-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Permissions et statuts</h3>
                            <div className="space-y-4">
                                <label className="flex items-center space-x-3">
                                    <input 
                                        type="checkbox" 
                                        name="active" 
                                        checked={user.active} 
                                        onChange={handleChange}
                                        className="w-5 h-5 text-primary bg-gray-100 border-gray-300 rounded focus:ring-primary focus:ring-2"
                                    />
                                    <span className="text-gray-700">
                                        <i className="fas fa-check-circle mr-2 text-green-500"></i>
                                        Utilisateur actif
                                    </span>
                                </label>
                                
                                <label className="flex items-center space-x-3">
                                    <input 
                                        type="checkbox" 
                                        name="is_admin" 
                                        checked={user.is_admin} 
                                        onChange={handleChange}
                                        className="w-5 h-5 text-primary bg-gray-100 border-gray-300 rounded focus:ring-primary focus:ring-2"
                                    />
                                    <span className="text-gray-700">
                                        <i className="fas fa-crown mr-2 text-yellow-500"></i>
                                        Administrateur
                                    </span>
                                </label>
                                
                                <label className="flex items-center space-x-3">
                                    <input 
                                        type="checkbox" 
                                        name="chefEntreprise" 
                                        checked={user.chefEntreprise} 
                                        onChange={handleChange}
                                        className="w-5 h-5 text-primary bg-gray-100 border-gray-300 rounded focus:ring-primary focus:ring-2"
                                    />
                                    <span className="text-gray-700">
                                        <i className="fas fa-briefcase mr-2 text-blue-500"></i>
                                        Chef d'entreprise
                                    </span>
                                </label>
                            </div>
                        </div>

                        <div className="flex gap-4 pt-6 border-t">
                            <button 
                                type="submit" 
                                className="btn-primary flex-1"
                            >
                                <i className="fas fa-save mr-2"></i>
                                Mettre à jour
                            </button>
                            <button 
                                type="button" 
                                onClick={handleDelete}
                                className="btn-danger flex-1"
                            >
                                <i className="fas fa-trash mr-2"></i>
                                Supprimer
                            </button>
                        </div>
                    </form>
                </div>
                
            {showModal && (
                <div className="modal-overlay" onClick={hide}>
                    <div className="modal-content">
                        <button 
                            className="absolute top-2 right-3 text-gray-500 hover:text-black text-xl"
                            onClick={hide}
                        >
                            ×
                        </button>
                        <div className="text-center">
                            <h2 className="text-2xl font-bold text-primary mb-4">Information</h2>
                            <p className="text-gray-700 mb-6">{message}</p>
                            <button 
                                onClick={hide}
                                className="btn-primary"
                            >
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            )}
            </div>
        </div>
    );
};

export default UserDetail;
